import { Link } from 'react-router-dom';
import {
  Icon,
  V2Badge,
  V2Card,
  V2CardBody,
  V2CardHeader,
  type V2BadgeTone,
} from '@sakhtar/design-system/react';
import type { QuoteFlow } from '../../lib/types/crm.js';
import { buildQuoteFlow, type FlowNextAction, type FlowStepState } from './quoteFlowModel.js';

const STATE_TONE: Record<FlowStepState, V2BadgeTone> = {
  done: 'success',
  current: 'brand',
  todo: 'neutral',
  locked: 'warn',
};

const STATE_LABEL: Record<FlowStepState, string> = {
  done: 'انجام شد',
  current: 'مرحلهٔ جاری',
  todo: 'در انتظار',
  locked: 'بدون دسترسی',
};

const NEXT_COPY: Record<FlowNextAction['kind'], string | null> = {
  send: 'گام بعد: پیش‌فاکتور را برای مشتری ارسال کنید.',
  accept: 'گام بعد: پذیرش مشتری را ثبت کنید.',
  convert: 'گام بعد: پیش‌فاکتور را به سفارش فروش تبدیل کنید (اقلام قابل ویرایش است).',
  deliver: 'گام بعد: موجودی را رزرو و کالا را تحویل دهید؛ فاکتور بر پایهٔ تحویل صادر می‌شود.',
  'issue-invoice': 'گام بعد: از صفحهٔ سفارش، فاکتور فروش را صادر کنید.',
  'submit-journal': 'گام بعد: سند درآمد پیش‌نویس را در مالی تأیید و ثبت قطعی کنید.',
  done: 'این پیش‌فاکتور تا ثبت قطعی در مالی پیش رفته است.',
  none: null,
};

/** The five stages from pre-invoice to finance, each linking to where it lives. */
export function QuoteFlowPanel({ flow, base }: { flow: QuoteFlow; base: string }) {
  const { steps, next } = buildQuoteFlow(flow);
  const nextCopy = NEXT_COPY[next.kind];
  return (
    <V2Card>
      <V2CardHeader title="مسیر پیش‌فاکتور تا مالی" subtitle={nextCopy ?? undefined} icon="trend" />
      <V2CardBody>
        <ol
          style={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
            display: 'grid',
            gap: 12,
            gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
          }}
        >
          {steps.map((step, index) => (
            <li
              key={step.key}
              aria-current={step.state === 'current' ? 'step' : undefined}
              style={{
                border: `1px solid ${step.state === 'current' ? 'var(--sk-brand)' : 'var(--sk-border)'}`,
                borderRadius: 'var(--sk-radius-md, 10px)',
                padding: 12,
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
                opacity: step.state === 'todo' ? 0.8 : 1,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span
                  aria-hidden="true"
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    display: 'grid',
                    placeItems: 'center',
                    fontSize: 12,
                    background:
                      step.state === 'done'
                        ? 'var(--sk-success)'
                        : 'var(--sk-surface-muted, #eef1f5)',
                    color: step.state === 'done' ? '#fff' : 'inherit',
                  }}
                >
                  {step.state === 'done' ? (
                    <Icon name="check" size="sm" />
                  ) : (
                    (index + 1).toLocaleString('fa-IR')
                  )}
                </span>
                <strong style={{ fontSize: 14 }}>{step.title}</strong>
              </div>
              <V2Badge tone={STATE_TONE[step.state]}>{STATE_LABEL[step.state]}</V2Badge>
              <span style={{ fontSize: 12, color: 'var(--sk-fg-muted)', lineHeight: 1.7 }}>
                {step.detail}
              </span>
              {step.links.map((link) => (
                <Link
                  key={`${link.to}:${link.label}`}
                  to={`${base}/${link.to}`}
                  style={{ fontSize: 12 }}
                >
                  {link.label} ←
                </Link>
              ))}
            </li>
          ))}
        </ol>
      </V2CardBody>
    </V2Card>
  );
}
